import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { SafeResourceUrl } from '@angular/platform-browser';
import { ApiRoute } from '@sb/library/api';
import { ProfileImageData, ProfileImageUploadData, ProfileImageSize, BodyRequired, DialogEvents, DialogManager, Environment, FieldOf, ImageOrientation, ItemWithID, OneParameterRequired, Orientation, ProductName, ProfileCardMode } from '@sb/library/base';
import { ProfilePictureInputItem } from '@sb/library/data-models';
import { ElevioService } from '@sb/library/elevio';
import { FormFieldBaseComponent } from '@sb/library/form-field';
import { DialogUploadingComponent, UploadDialogData } from '@sb/library/upload';
import { Observable, of, switchMap } from 'rxjs';
import { ProfileImageService } from './profile-image.service';
import { ProfileImageUploadDialogComponent } from './upload-dialog/profile-image-upload-dialog.component';

@Component({
    selector: 'lib-profile-image',
    templateUrl: './profile-image.component.html',
    styleUrls: ['./profile-image.component.scss']
})
export class ProfileImageComponent<T extends ItemWithID> extends FormFieldBaseComponent<ProfileImageData | null, FormControl<ProfileImageData>> implements OnInit {

    @Input() size: ProfileImageSize = ProfileImageSize.Medium;
    @Input() showName: boolean = false;
    @Input() editable: boolean = true;
    @Input() updateRoute!: ApiRoute<OneParameterRequired & BodyRequired<FormData>, string>;
    @Input() deleteRoute!: ApiRoute<OneParameterRequired, boolean>;
    @Input() previewRoute!: ApiRoute<BodyRequired<FormData>, string>
    @Input() hideAvatar: boolean = false;
    @Input() mode: ProfileCardMode = ProfileCardMode.Avatar;
    @Input() elevioArticleNumberForIncorrectFiles?: number;
    @Input() orientation: Orientation = this.Orientation.Vertical;

    private _isInitialized: boolean = false;
    private _displayItem!: T;
    private _loadedImageKeys: Set<string> = new Set();
    @Input()
    set displayItem(value: T) {
        const displayItemChanged = this._displayItem !== value;
        this._displayItem = value;
        if (value) {
            this._ProfileImageService.SetValueForHasImage(this.displayItem.ID!, this._hasImageToShow);
            if (displayItemChanged) {
                this.SetImageLoading((this._isInitialized && this.isLoading) || this._hasImageToShow);
            }
            this.ProfileImageSetup();
        }
    }

    private _hasImageToShow!: boolean;
    @Input()
    set hasImageToShow(value: boolean) {
        this._hasImageToShow = value;
        if (value) {
            this.SetImageLoading(true);
        } else if (!this._isInitialized || !this.isLoading) {
            this.SetImageLoading(false);
        }
        if (this._displayItem) {
            this._ProfileImageService.SetValueForHasImage(this.displayItem.ID!, value);
            this.ProfileImageSetup();
        }
    }

    private _imageURL: string = '';
    @Input()
    set imageURL(value: string) {
        //do cache busting here
        const imageURLChanged = value !== this._imageURL;
        this._imageURL = value;
        if (imageURLChanged && this.hasImageToShow) {
            this.SetImageLoading(true);
        }
    }

    private _nameBy?: FieldOf<T>;
    @Input()
    set nameBy(value: FieldOf<T>) {
        this._nameBy = value;
    }

    @Output() editClicked: EventEmitter<void> = new EventEmitter();

    private _initials: string = '';
    private _name: string = '';

    private _backgroundColor!: string;

    private _colors: Array<string> = [
        'background-beach',
        'background-black',
        'background-blue',
        'background-brown',
        'background-darkorange',
        'background-gray',
        'background-green',
        'background-grassgreen',
        'background-lavender',
        'background-lightgrey',
        'background-lightpink',
        'background-lime',
        'background-magenta',
        'background-mint',
        'background-olive',
        'background-orange',
        'background-pink',
        'background-purple',
        'background-red',
        'background-royal',
        'background-ruby',
        'background-salmon',
        'background-seablue',
        'background-tan',
        'background-tangerine',
        'background-teal',
        'background-yellow',
        'background-white'
    ];

    private _currentOrientation = ImageOrientation.RegularHorizontal;
    private _imagePreview: string | null = null;
    _imageLoading: boolean = false;

    constructor(private _ProfileImageService: ProfileImageService,
        private _ElevioService: ElevioService,
        private _DialogManager: DialogManager,
        private _Environment: Environment) {
        super();
        this.viewControl = new FormControl();
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this._isInitialized = true;
        this.SetImageLoading(this.isLoading || this._hasImageToShow);
        if (this.displayItem) {
            this._ProfileImageService.SetValueForHasImage(this.displayItem.ID!, this._hasImageToShow);
            if (!this.formControl.value) {
                this.formControl.setValue(new ProfileImageData());
            }
            this.ProfileImageSetup();
        }

    }

    private ProfileImageSetup(): void {
        this.SetInitials();
        this.GetBackgroundColor();
    }

    private SetInitials(): void {
        const keys = Object.keys(this.displayItem);
        const hasFirstNameField = keys.includes('FirstName');

        if (keys.includes('FullName')) {
            this._nameBy = 'FullName' as FieldOf<T>;
        } else if (keys.includes('Name')) {
            this._nameBy = 'Name' as FieldOf<T>;
        } else {
            throw Error(`Could not determine a field to use as a name when setting up (${this.constructor.name}).`)
        }

        this._name = (this.displayItem[this._nameBy!] as unknown as string) || '';

        let firstName = "";
        let lastName = "";
        if (hasFirstNameField) {
            firstName = (this.displayItem['FirstName' as FieldOf<T>] as unknown as string) || '';
            lastName = (this.displayItem['LastName' as FieldOf<T>] as unknown as string) || '';
        } else {
            const split = this._name?.split(/[ ,]+/) || [];
            firstName = split[0] || '';
            if (split.length > 1) {
                lastName = split[1] || '';
            }
        }

        const firstInitial = Array.from((firstName?.trim() || ''))[0] ?? "";
        const lastInitial = Array.from((lastName?.trim() || ''))[0] ?? "";

        this._initials = this._name && this._name.includes(',') ? lastInitial + firstInitial : firstInitial + lastInitial;
    }

    private GetBackgroundColor(): void {
        const isLocalOrTest = this._Environment.Application?.ProductName === ProductName.Local
            || this._Environment.Application?.ProductName === ProductName.Test443;
        const seed = isLocalOrTest ? (this._name ?? this._displayItem.ID!) : this.displayItem.ID!;
        const hash = [...seed].reduce((sum, c) => sum + c.charCodeAt(0), 0);

        let backgroundColor = this._colors[hash % this._colors.length];

        if (backgroundColor === 'background-white') {
            const colors = this._colors.filter(c => c !== 'background-white');
            backgroundColor = colors[hash % colors.length];
        }

        this._backgroundColor = backgroundColor;
    }

    EditClicked(): void {
        this.editClicked.emit();
    }

    AddOrEditProfileImage(): void {
        this.OpenProfilePictureUploadDialog();
    }

    private OpenProfilePictureUploadDialog(): void {
        const uploadDialog = this.CreateDialog(ProfileImageUploadDialogComponent, new UploadDialogData({
            AllowMultiFileUpload: false,
            AcceptedFormat: this.UploadFileType.ProfileImages,
            SnackbarAction: () => {
                this._ElevioService.OpenElevioArticle(this.elevioArticleNumberForIncorrectFiles!);
            },
        }));

        uploadDialog.upload.subscribe((data: ProfileImageUploadData) => {
            this.PrepareDataToUpload(data);
        });

        uploadDialog.events.subscribe((e: DialogEvents) => {
            switch (e) {
                case DialogEvents.Dismissed:
                    break;
                case DialogEvents.PrimaryAction:
                    this.OpenUploadingDialog();
                    uploadDialog.DismissAction();
                    break;
            }
        });
    }

    private PrepareDataToUpload(data: ProfileImageUploadData) {
        this.formControl.value!.Data = [];
        data.Files.forEach(file => {
            this.formControl.value!.Data!.push({
                ID: this.displayItem.ID!,
                File: file,
                InputItem: new ProfilePictureInputItem({
                    UseFaceDetection: data.UseFaceDetection
                })
            });
        });
    }

    UpdateProfileImage(): Observable<boolean> {
        const uploadDialog = this.CreateDialog(DialogUploadingComponent, {
            Title: $localize`:@@AvatarUploadingProfilePictureLabel:Uploading Profile Picture`,
            Files: this.formControl.value!.Data,
            DeleteOnCancel: false,
            ApiSubmitRoute: this.updateRoute,
        });

        return uploadDialog.uploadSuccess.pipe(switchMap(() => {
            uploadDialog.successfulUploadedFilesData.forEach(_  => {
                this._imagePreview = null;
                this._ProfileImageService.SetValueForHasImage(this.displayItem.ID!, true);
                this._ProfileImageService.RemoveCacheValue(this.displayItem.ID!);
                this.SetImageLoading(true);
            });
            return of(true);
        }))

    }

    private OpenUploadingDialog(): void {
        const uploadDialog = this.CreateDialog(DialogUploadingComponent, {
            Title: $localize`:@@AvatarGeneratingPreviewLabel:Generating Preview`,
            Files: this.formControl.value!.Data,
            DeleteOnCancel: false,
            ApiSubmitRoute: this.previewRoute,
        });

        uploadDialog.uploadSuccess.subscribe(base64Image => {
            this._imagePreview = `data:image/png;base64, ${base64Image[0].ID}`;
            this.SetImageLoading(true);
            this.formControl.isDirty = true;
            this.formControl.value!.Deleted = false;
        });
    }

    DeletePreviewProfileImage(): void {
        this.formControl.value!.Deleted = true;
        this.formControl.value!.Data = undefined;
        this._imagePreview = null;
        this.formControl.isDirty = true;
    }

    DeleteProfileImage(): Observable<boolean> {
        return this.deleteRoute.Call({Parameter: this.displayItem.ID!}).pipe(switchMap(() => {
            this._imageLoading = false;
            this._ProfileImageService.SetValueForHasImage(this.displayItem.ID!, false);
            this.ProfileImageSetup();
            this._ProfileImageService.RemoveCacheValue(this.displayItem.ID!);
            this._DialogManager.DismissDialog();
            return of(true);
        }));
    }

    GetImageOrientation(orientation: ImageOrientation): void {
        this._currentOrientation = orientation;
    }

    GetImage(profileURL: SafeResourceUrl, imagePreview: string | null): SafeResourceUrl | string {
        return imagePreview ? imagePreview : profileURL;
    }

    ImageLoaded(event: Event): void {
        const image = event.currentTarget as HTMLImageElement;
        if (image.currentSrc && image.currentSrc !== image.src) {
            return;
        }
        const imageKey = this.GetCurrentImageKey();
        if (event.type === 'load' && imageKey) {
            this._loadedImageKeys.add(imageKey);
        }
        this.SetImageLoading(false);
    }

    private SetImageLoading(value: boolean): void {
        this._imageLoading = value && !this.HasImageLoaded();
    }

    private HasImageLoaded(): boolean {
        const imageKey = this.GetCurrentImageKey();
        return imageKey !== null && this._loadedImageKeys.has(imageKey);
    }

    private GetCurrentImageKey(): string | null {
        const displayItemID = this._displayItem?.ID;
        if (!displayItemID) {
            return null;
        }
        return JSON.stringify([displayItemID, this._imagePreview ?? this.imageURL]);
    }

    get backgroundColor(): string {
        return this._backgroundColor;
    }

    get imageURL(): string {
        return this._ProfileImageService.GetCacheValue(this.displayItem.ID!,this._imageURL);
    }

    get initials(): string {
        return this._initials;
    }

    get name(): string {
        return this._name;
    }

    get hasImageToShow(): boolean {
        return this._ProfileImageService.GetHasImage(this.displayItem?.ID!);
    }

    get displayItem(): T {
        return this._displayItem;
    }

    get ProfileCardMode(): typeof ProfileCardMode {
        return ProfileCardMode;
    }

    get currentOrientation(): ImageOrientation {
        return this._currentOrientation;
    }

    get ImageOrientation(): typeof ImageOrientation {
        return ImageOrientation;
    }

    get imagePreview(): string | null {
        return this._imagePreview;
    }

    get imageLoading(): boolean {
        return !this.HasImageLoaded() && (this._imageLoading || this.isLoading);
    }

    get isSoftDeleted(): boolean {
        return this.formControl.value ? this.formControl.value.Deleted : false;
    }
}
